'use client';
import { useCallback, useMemo, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { api } from '@r-and-a-shared-ui/randa-trpc-client';
import { FilterBlockOptions } from '../FilterBlock/FilterBlock';

export const useArticleFilter = () => {
  const { data } = api.kontent.articleAuthors.useQuery();
  const router = useRouter();

  const [isRouting, setIsRouting] = useState(false);

  useEffect(() => {
    const start = () => setIsRouting(true);
    const end = () => setIsRouting(false);
    router.events?.on('routeChangeStart', start);
    router.events?.on('routeChangeComplete', end);
    router.events?.on('routeChangeError', end);
    return () => {
      router.events?.off('routeChangeStart', start);
      router.events?.off('routeChangeComplete', end);
      router.events?.off('routeChangeError', end);
    };
  }, [router.events]);

  const authors = useMemo(
    () =>
      data?.authors?.map(author => ({
        name: `${author.elements.name.value} ${author.elements.surname.value}`,
        codename: author.system.codename,
      })) || [],
    [data],
  );

  const authorFilterOptions = useMemo(
    () => authors.map(author => author.name),
    [authors],
  );

  const activeFilters = useMemo(() => {
    const q = router.query?.author;
    const authorCodenames = Array.isArray(q) ? q : q ? [q] : [];
    const names = authorCodenames
      .map(cn => authors.find(a => a.codename === cn)?.name || null)
      .filter(Boolean) as string[];
    return names;
  }, [router.query, authors]);

  const handleApplyFilter = useCallback(
    async (value: FilterBlockOptions): Promise<void> => {
      const filteredCodeNames = value.Authors
        .map(name => authors.find(a => a.name === name)?.codename || null)
        .filter(Boolean) as string[];

      const url = {
        pathname: router.pathname,
        query: filteredCodeNames.length ? { author: filteredCodeNames } : undefined,
      };

      await router.push(url);
    },
    [router, authors],
  );

  return {
    filterOption: authorFilterOptions,
    activeFilters,
    handleApplyFilter,
    isRouting,
  };
};